import { useState } from "react";
import type { IconType } from "react-icons";
import { FaApple, FaLinux, FaWindows } from "react-icons/fa";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import {
  detectInstallerOs,
  downloadInstaller,
  INSTALLER_OS_LABELS,
  LINUX_FORMATS,
  type InstallerOs,
  type LinuxFormat,
} from "@/features/support-platform/downloadInstaller";

const OS_ICONS: Record<InstallerOs, IconType> = {
  windows: FaWindows,
  macos: FaApple,
  linux: FaLinux,
};

const ALL_OS = Object.keys(INSTALLER_OS_LABELS) as InstallerOs[];

function OsName({ os, current = false }: { os: InstallerOs; current?: boolean }) {
  const Icon = OS_ICONS[os];
  // flex-1 keeps the badge at the row's end even where a submenu arrow also sits there.
  return (
    <span className="flex flex-1 items-center gap-2">
      <Icon aria-hidden className="size-4 text-muted-foreground" />
      {INSTALLER_OS_LABELS[os]}
      {current ? (
        <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
          Current
        </span>
      ) : null}
    </span>
  );
}

type StartDownload = (os: InstallerOs, format?: LinuxFormat) => void;

function LinuxFormatItems({ onDownload, inset = false }: { onDownload: StartDownload; inset?: boolean }) {
  return LINUX_FORMATS.map((option) => (
    <DropdownMenuItem
      key={option.format}
      onSelect={() => onDownload("linux", option.format)}
      className={`flex-col items-start gap-0 ${inset ? "pl-8" : ""}`}
    >
      <span>{option.label}</span>
      <span className="text-xs text-muted-foreground">{option.hint}</span>
    </DropdownMenuItem>
  ));
}

/** An OS picked from the menu: one click, or a choice of formats for Linux. */
function OsItem({ os, current, onDownload }: { os: InstallerOs; current?: boolean; onDownload: StartDownload }) {
  if (os !== "linux") {
    return (
      <DropdownMenuItem onSelect={() => onDownload(os)}>
        <OsName os={os} current={current} />
      </DropdownMenuItem>
    );
  }
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <OsName os={os} current={current} />
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <LinuxFormatItems onDownload={onDownload} />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

/**
 * "Support" under Platforms. Offers the installer for the computer this page is open on, with the
 * other platforms tucked away so the usual choice is the obvious one.
 */
export function SupportPlatformNavItem() {
  const [downloading, setDownloading] = useState(false);
  const detected = detectInstallerOs();
  const others = ALL_OS.filter((os) => os !== detected);

  const download: StartDownload = async (os, format) => {
    const formatLabel = LINUX_FORMATS.find((option) => option.format === format)?.label;
    const label = formatLabel
      ? `${INSTALLER_OS_LABELS[os]} (${formatLabel})`
      : INSTALLER_OS_LABELS[os];
    setDownloading(true);
    try {
      const version = await downloadInstaller(os, format);
      toast.success(`Downloading Support Platform ${version} for ${label}.`);
    } catch {
      toast.error(`The ${label} installer could not be downloaded.`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        {/* The sidebar button renders as the menu trigger, so its tooltip and the menu share one
            real button; the other way round the trigger would attach to the tooltip wrapper. */}
        <SidebarMenuButton asChild tooltip="Support Platform">
          <DropdownMenuTrigger disabled={downloading}>
            {downloading ? <Loader2 className="animate-spin" /> : <Download />}
            <span>Support</span>
          </DropdownMenuTrigger>
        </SidebarMenuButton>
        <DropdownMenuContent side="right" align="start" className="min-w-52">
          <DropdownMenuLabel>Select your OS</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {detected === null ? (
            // Unknown computer: nothing to put first, so offer everything.
            ALL_OS.map((os) => <OsItem key={os} os={os} onDownload={download} />)
          ) : (
            <>
              {detected === "linux" ? (
                <>
                  <DropdownMenuLabel className="flex font-normal">
                    <OsName os="linux" current />
                  </DropdownMenuLabel>
                  <LinuxFormatItems onDownload={download} inset />
                </>
              ) : (
                <OsItem os={detected} current onDownload={download} />
              )}
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="text-muted-foreground">
                  Other platforms
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {others.map((os) => (
                    <OsItem key={os} os={os} onDownload={download} />
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
